import { BadgeCore, BadgeCoreProps } from "./BadgeCore";
import { SquareLockFilledIcon, FireFilledIcon, HeartFilledIcon } from "app/icons/navigation";
import { TourFilledIcon, BaggageLFilledIcon } from "app/icons/travel";
import { TagFilledIcon } from "app/icons/finance";
import { useIntl } from "react-intl";
import TradeDownIcon from "app/icons/navigation/TradeDownIcon";

type Preset = {
	color: string;
	accentColor?: string;
	icon?: React.ElementType;
	primaryText: string;
	secondaryText?: string;
};

export type BadgeItemType =
	| "membersOnly"
	| "limited"
	| "lastDays"
	| "topPicks"
	| "tours"
	| "discount"
	| "luggage"
	| "quote"
	| "priceDrop";

const BADGE_PRESETS: Record<BadgeItemType, Preset> = {
	membersOnly: {
		color: "var(--badge-members-only-color)",
		icon: SquareLockFilledIcon,
		primaryText: "product.unbeatable.offer.label",
		secondaryText: "product.unbeatable.offer.access.limited.label",
	},
	limited: {
		color: "var(--color-warning-600)",
		accentColor: "var(--color-warning-500)",
		icon: FireFilledIcon,
		primaryText: "product.unbeatable.offer.label",
		secondaryText: "product.unbeatable.offer.LIMITED_TIME",
	},
	lastDays: {
		color: "var(--color-negative-600)",
		accentColor: "var(--color-negative-500)",
		icon: FireFilledIcon,
		primaryText: "product.unbeatable.offer.label",
		secondaryText: "product.unbeatable.offer.LAST_DAY",
	},
	topPicks: {
		color: "#ed4982",
		icon: HeartFilledIcon,
		primaryText: "product.isFavorite",
	},
	tours: {
		color: "var(--color-black)",
		icon: TourFilledIcon,
		primaryText: "multi.stage.journey",
	},
	discount: {
		color: "var(--discount-color, var(--button-primary-background-color, $button-primary-background-color))",
		icon: TagFilledIcon,
		primaryText: "badge.savings.per.person",
	},
	luggage: {
		color: "var(--button-primary-background-color, $button-primary-background-color)",
		icon: BaggageLFilledIcon,
		primaryText: "product.baggage.free.tag",
	},
	quote: { color: "var(--color-black)", primaryText: "listing.ongoing.quote" },
	priceDrop: {
		color: "var(--button-primary-background-color, $button-primary-background-color)",
		icon: TradeDownIcon,
		primaryText: "general.decreasing.price.default.label",
	},
};

type BadgeItemProps = {
	type: BadgeItemType;
	showSecondaryText?: boolean;
	primaryValues?: Record<string, string | number>;
	secondaryValues?: Record<string, string | number>;
} & BadgeCoreProps;

export const BadgeItem = ({
	type,
	showSecondaryText = true,
	primaryValues,
	secondaryValues,
	...props
}: BadgeItemProps) => {
	const { formatMessage } = useIntl();

	const {
		color,
		accentColor: rawAccentColor,
		icon: Icon,
		primaryText: primaryId,
		secondaryText: secondaryId,
	} = BADGE_PRESETS[type];

	const accentColor = rawAccentColor ?? color;
	const icon = Icon ? (
		<Icon size={16} width={16} height={16} data-testid="badge-icon" />
	) : undefined;

	const primaryText =
		!showSecondaryText && secondaryId
			? formatMessage({ id: secondaryId }, secondaryValues)
			: formatMessage({ id: primaryId }, primaryValues);

	const secondaryText = secondaryId
		? formatMessage({ id: secondaryId }, secondaryValues)
		: undefined;

	return (
		<BadgeCore
			color={color}
			accentColor={accentColor}
			icon={icon}
			primaryText={primaryText}
			secondaryText={secondaryText}
			showSecondaryText={showSecondaryText}
			{...props}
		/>
	);
};
